/**
 * Geometry-type resolution.
 *
 * Geometry NEVER comes from the layer name — it is read from service metadata:
 *   ArcGIS  → layer metadata `geometryType`
 *   GeoServer → WFS DescribeFeatureType (WMS GetCapabilities omits it)
 *
 * This matters because the symbology UI is chosen from it. Guessing wrong means
 * showing point symbols for a line layer.
 */

import type { AppModule, Layer, LayerGeometryType } from '../types';
import { arcgisServiceUrlFor, getArcgisBaseUrl, withArcgisToken } from '../utils/arcgis';
import { normaliseGeometryType } from './capabilities';
import { detectWfsGeometryType } from './sld';

/**
 * Returns the cached geometry type, or fetches it from the service.
 *
 * Layers added before geometry capture existed have no cached value, so the
 * panel resolves on demand rather than falling back to a default that would
 * render the wrong editor.
 */
export async function resolveGeometryType(
  layer: Layer,
  module: AppModule,
): Promise<LayerGeometryType> {
  if (layer.geometryType && layer.geometryType !== 'unknown') return layer.geometryType;

  // ── ArcGIS ──
  if (layer.arcgisLayerId !== undefined) {
    const effectiveUrl = arcgisServiceUrlFor(layer, module);
    const baseUrl = getArcgisBaseUrl(effectiveUrl);
    if (!baseUrl) return 'unknown';
    try {
      const response = await fetch(withArcgisToken(`${baseUrl}/${layer.arcgisLayerId}?f=json`, effectiveUrl));
      if (!response.ok) {
        console.error(
          `[geometry] ArcGIS metadata failed (${response.status}) layer="${layer.name}"`,
        );
        return 'unknown';
      }
      const data = await response.json();
      if (data?.error) {
        console.error(`[geometry] ArcGIS error layer="${layer.name}": ${data.error.message}`);
        return 'unknown';
      }
      return normaliseGeometryType(data.geometryType);
    } catch (error) {
      console.error(`[geometry] ArcGIS request failed layer="${layer.name}"`, error);
      return 'unknown';
    }
  }

  // ── GeoServer ──
  if (module.geoServerUrl) {
    return detectWfsGeometryType(module.geoServerUrl, layer.name);
  }

  return 'unknown';
}
